import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Briefcase, User, Inbox, Send, MapPin, Clock, Euro, ArrowRight, Mail, Phone, ShieldCheck, Rocket, Eye, Star, Crown, Gift, Copy, Share2, Award } from "lucide-react";
import StarRating from "@/components/StarRating";
import ReviewModal from "@/components/ReviewModal";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [myJobs, setMyJobs] = useState([]);
  const [myProfiles, setMyProfiles] = useState([]);
  const [received, setReceived] = useState([]);
  const [sent, setSent] = useState([]);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [reviewing, setReviewing] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  const [referral, setReferral] = useState(null);
  const [founding, setFounding] = useState(null);

  const loadPending = () => api.get("/reviews/pending").then((r) => setPendingReviews(r.data)).catch(() => {});

  useEffect(() => {
    const load = async () => {
      const reqs = [
        api.get("/applications/received").then((r) => setReceived(r.data)).catch(() => {}),
        api.get("/applications/sent").then((r) => setSent(r.data)).catch(() => {}),
        loadPending(),
      ];
      if (user.role === "employer" || user.role === "admin")
        reqs.push(api.get("/jobs/mine").then((r) => setMyJobs(r.data)).catch(() => {}));
      if (user.role === "worker" || user.role === "admin")
        reqs.push(api.get("/workers/mine").then((r) => setMyProfiles(r.data)).catch(() => {}));
      reqs.push(api.get("/referrals/me").then((r) => setReferral(r.data)).catch(() => {}));
      reqs.push(api.get("/founding-member/status").then((r) => setFounding(r.data)).catch(() => {}));
      await Promise.all(reqs);
    };
    if (user) load();
  }, [user]);

  const upgradeToPro = async () => {
    setUpgrading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: "employer_pro_monthly",
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
      setUpgrading(false);
    }
  };

  const upgradeToWorkerPro = async () => {
    setUpgrading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: "worker_pro_monthly",
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
      setUpgrading(false);
    }
  };

  const buyFoundingMember = async () => {
    setUpgrading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: "founding_member",
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
      setUpgrading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
      <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
        <div>
          <div className="text-amber-500 text-xs font-bold tracking-[0.3em] uppercase">Dashboard</div>
          <h1 className="display text-4xl md:text-5xl mt-2">Hey {user.name.split(" ")[0]}</h1>
          <div className="text-neutral-400 text-sm mt-2 uppercase tracking-wide">{user.role} · {user.email}</div>
        </div>
        <div className="flex gap-3">
          {(user.role === "employer" || user.role === "admin") && (
            <Link data-testid="dash-post-job" to="/jobs/new" className="px-4 py-2 bg-amber-500 text-neutral-950 text-xs font-bold uppercase tracking-widest hover:bg-amber-400 transition-colors">Post Job</Link>
          )}
          {(user.role === "worker" || user.role === "admin") && (
            <Link data-testid="dash-post-avail" to="/workers/new" className="px-4 py-2 bg-amber-500 text-neutral-950 text-xs font-bold uppercase tracking-widest hover:bg-amber-400 transition-colors">Post Availability</Link>
          )}
        </div>
      </div>

      {/* Pro upgrade banner (employers only) */}
      {(user.role === "employer" || user.role === "admin") && !user.is_pro && (
        <div data-testid="pro-upgrade-banner" className="mb-6 border border-amber-500 bg-gradient-to-r from-amber-500/10 to-transparent p-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-start gap-4">
            <Crown className="w-8 h-8 text-amber-500 shrink-0" strokeWidth={2} />
            <div>
              <div className="display text-2xl">Upgrade to <span className="text-amber-500">Employer Pro</span></div>
              <div className="text-sm text-neutral-400 mt-1">Unlimited job posts + &ldquo;who viewed my job&rdquo; analytics + priority support · €49/mo</div>
            </div>
          </div>
          <button data-testid="upgrade-pro-btn" onClick={upgradeToPro} disabled={upgrading} className="px-6 py-3 bg-amber-500 text-neutral-950 text-xs font-bold uppercase tracking-widest hover:bg-amber-400 transition-colors disabled:opacity-60 shrink-0">
            {upgrading ? "Redirecting…" : "Upgrade — €49/mo"}
          </button>
        </div>
      )}
      {user.is_pro && (
        <div data-testid="pro-active-banner" className="mb-6 border border-amber-500 bg-amber-500/5 p-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Crown className="w-5 h-5 text-amber-500" />
            <div className="text-sm">
              <span className="display text-lg text-amber-500">Employer Pro Active</span>
              <span className="text-neutral-400 ml-2 text-xs uppercase tracking-widest">Until {user.pro_until ? new Date(user.pro_until).toLocaleDateString() : "—"}</span>
            </div>
          </div>
          <button
            data-testid="manage-subscription-btn"
            onClick={async () => {
              try {
                const { data } = await api.post("/payments/portal", { return_url: window.location.origin + "/dashboard" });
                window.location.href = data.url;
              } catch (e) {
                toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to open portal");
              }
            }}
            className="text-xs uppercase tracking-widest text-neutral-400 hover:text-amber-500 px-3 py-1.5 border border-neutral-800 hover:border-amber-500 transition-colors"
          >
            Manage subscription
          </button>
        </div>
      )}

      {/* Worker Pro upgrade banner (workers only) */}
      {(user.role === "worker" || user.role === "admin") && !user.is_worker_pro && (
        <div data-testid="worker-pro-upgrade-banner" className="mb-6 border border-amber-500 bg-gradient-to-r from-amber-500/10 to-transparent p-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-start gap-4">
            <Rocket className="w-8 h-8 text-amber-500 shrink-0" strokeWidth={2} />
            <div>
              <div className="display text-2xl">Go <span className="text-amber-500">Worker Pro</span></div>
              <div className="text-sm text-neutral-400 mt-1">Unlimited applications + reveal employer contact + priority listing · €9/mo</div>
            </div>
          </div>
          <button data-testid="upgrade-worker-pro-btn" onClick={upgradeToWorkerPro} disabled={upgrading} className="px-6 py-3 bg-amber-500 text-neutral-950 text-xs font-bold uppercase tracking-widest hover:bg-amber-400 transition-colors disabled:opacity-60 shrink-0">
            {upgrading ? "Redirecting…" : "Subscribe — €9/mo"}
          </button>
        </div>
      )}
      {user.is_worker_pro && (
        <div data-testid="worker-pro-active-banner" className="mb-6 border border-amber-500 bg-amber-500/5 p-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Rocket className="w-5 h-5 text-amber-500" />
            <div className="text-sm">
              <span className="display text-lg text-amber-500">Worker Pro Active</span>
              <span className="text-neutral-400 ml-2 text-xs uppercase tracking-widest">Until {user.worker_pro_until ? new Date(user.worker_pro_until).toLocaleDateString() : "—"}</span>
            </div>
          </div>
          <button
            data-testid="manage-worker-subscription-btn"
            onClick={async () => {
              try {
                const { data } = await api.post("/payments/portal", { return_url: window.location.origin + "/dashboard" });
                window.location.href = data.url;
              } catch (e) {
                toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to open portal");
              }
            }}
            className="text-xs uppercase tracking-widest text-neutral-400 hover:text-amber-500 px-3 py-1.5 border border-neutral-800 hover:border-amber-500 transition-colors"
          >
            Manage subscription
          </button>
        </div>
      )}

      {/* Referral card */}
      {referral && (
        <div data-testid="referral-card" className="mb-6 border border-neutral-800 bg-neutral-900 p-5">
          <div className="flex items-start gap-4 flex-wrap">
            <Gift className="w-8 h-8 text-amber-500 shrink-0" strokeWidth={2} />
            <div className="flex-1 min-w-[240px]">
              <div className="display text-2xl">Invite mates, <span className="text-amber-500">bank free months</span></div>
              <div className="text-sm text-neutral-400 mt-1">
                They save <span className="text-amber-400 font-bold">€5</span> off their first month.
                You get <span className="text-amber-400 font-bold">a free month</span> added when they subscribe.
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Successful referrals</div>
              <div className="display text-3xl text-amber-500 mt-1">{String(referral.referred_count || 0).padStart(2, "0")}</div>
            </div>
          </div>
          <div className="mt-5 grid md:grid-cols-[1fr_auto_auto] gap-2">
            <div className="flex items-center gap-3 bg-neutral-950 border border-neutral-800 px-4 py-3">
              <span className="text-[10px] uppercase tracking-widest text-neutral-500">Your code</span>
              <span data-testid="referral-code" className="font-mono text-amber-400 font-bold tracking-widest">{referral.code}</span>
            </div>
            <button
              data-testid="referral-copy-btn"
              onClick={() => {
                navigator.clipboard.writeText(referral.share_url);
                toast.success("Share link copied");
              }}
              className="px-4 py-3 border border-neutral-800 hover:border-amber-500 text-xs uppercase tracking-widest text-neutral-300 hover:text-amber-500 inline-flex items-center gap-2"
            >
              <Copy className="w-3.5 h-3.5" /> Copy link
            </button>
            <a
              data-testid="referral-share-whatsapp"
              href={`https://wa.me/?text=${encodeURIComponent(`I've been using SiteCrews for shifts in Ireland. Use my code ${referral.code} at signup and you'll get €5 off. ${referral.share_url}`)}`}
              target="_blank"
              rel="noreferrer"
              className="px-4 py-3 bg-amber-500 text-neutral-950 text-xs font-bold uppercase tracking-widest hover:bg-amber-400 inline-flex items-center gap-2"
            >
              <Share2 className="w-3.5 h-3.5" /> Share on WhatsApp
            </a>
          </div>
        </div>
      )}

      {/* Founding Member — one-off €29 lifetime */}
      {founding && !user.founding_member && founding.remaining > 0 && (
        <div data-testid="founding-member-banner" className="mb-6 border border-amber-500 bg-gradient-to-br from-amber-500/15 via-neutral-900 to-neutral-900 p-6">
          <div className="flex items-start gap-4 flex-wrap">
            <Award className="w-10 h-10 text-amber-500 shrink-0" strokeWidth={2} />
            <div className="flex-1 min-w-[260px]">
              <div className="text-[10px] uppercase tracking-[0.3em] text-amber-500 font-bold">Only {founding.remaining} spots left · Limited to 100 people</div>
              <div className="display text-3xl mt-1">Become a <span className="text-amber-500">Founding Member</span></div>
              <div className="text-sm text-neutral-300 mt-2 max-w-2xl">
                One-off <span className="text-amber-400 font-bold">€29</span> · <span className="font-bold">lifetime Pro access</span> (never pay monthly), a golden Founding badge on your profile, priority support, and early access to every new feature.
              </div>
            </div>
            <button
              data-testid="founding-buy-btn"
              onClick={buyFoundingMember}
              disabled={upgrading}
              className="px-6 py-3 bg-amber-500 text-neutral-950 text-xs font-bold uppercase tracking-widest hover:bg-amber-400 transition-colors disabled:opacity-60 shrink-0"
            >
              {upgrading ? "Redirecting…" : `Claim spot · €29`}
            </button>
          </div>
          <div className="mt-4 pt-4 border-t border-amber-500/30">
            <div className="flex items-center gap-3">
              <div className="flex-1 h-1.5 bg-neutral-800 relative">
                <div
                  className="absolute inset-y-0 left-0 bg-amber-500"
                  style={{ width: `${(founding.claimed / founding.cap) * 100}%` }}
                />
              </div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 whitespace-nowrap">
                <span className="text-amber-500 font-bold">{founding.claimed}</span> / {founding.cap} claimed
              </div>
            </div>
          </div>
        </div>
      )}
      {user.founding_member && (
        <div data-testid="founding-member-active" className="mb-6 border border-amber-500 bg-amber-500/10 p-4 flex items-center gap-3 flex-wrap">
          <Award className="w-6 h-6 text-amber-500" />
          <div className="text-sm">
            <span className="display text-lg text-amber-500">Founding Member #{user.founding_number}</span>
            <span className="text-neutral-400 ml-2 text-xs uppercase tracking-widest">Lifetime Pro · thank you for backing us early</span>
          </div>
        </div>
      )}

      {/* Pending reviews strip */}
      {pendingReviews.length > 0 && (
        <div data-testid="pending-reviews" className="mb-6 border border-neutral-800 bg-neutral-900 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Star className="w-4 h-4 text-amber-500" />
            <div className="text-xs font-bold uppercase tracking-widest">Leave a review · {pendingReviews.length} pending</div>
          </div>
          <div className="flex flex-wrap gap-2">
            {pendingReviews.slice(0, 6).map((p) => (
              <button
                key={p.application_id}
                data-testid={`pending-review-${p.application_id}`}
                onClick={() => setReviewing(p)}
                className="px-3 py-2 border border-neutral-800 hover:border-amber-500 hover:text-amber-500 text-xs uppercase tracking-wide transition-colors"
              >
                Rate {p.other_name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Bento */}
      <div className="grid md:grid-cols-12 gap-4">
        <Stat label="My Jobs" value={myJobs.length} Icon={Briefcase} span="md:col-span-3" show={user.role !== "worker"} />
        <Stat label="My Profiles" value={myProfiles.length} Icon={User} span="md:col-span-3" show={user.role !== "employer"} />
        <Stat label="Applications In" value={received.length} Icon={Inbox} span="md:col-span-3" />
        <Stat label="Applications Sent" value={sent.length} Icon={Send} span="md:col-span-3" />

        {(user.role === "employer" || user.role === "admin") && (
          <Panel title="My Job Posts" empty="You have no jobs posted yet." span="md:col-span-6" data={myJobs} testid="my-jobs-panel">
            {myJobs.map((j) => (
              <Link key={j.id} to={`/jobs/${j.id}`} data-testid={`my-job-${j.id}`} className="group flex items-start justify-between gap-4 p-4 border-b border-neutral-800 last:border-0 hover:bg-neutral-900 transition-colors">
                <div className="min-w-0">
                  <div className="text-xs font-bold uppercase tracking-widest text-amber-500">{j.trade}</div>
                  <div className="display text-lg group-hover:text-amber-500 transition-colors truncate">{j.title}</div>
                  <div className="text-xs uppercase tracking-wide text-neutral-400 mt-1 flex gap-3 flex-wrap">
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{j.county}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{j.duration}</span>
                    <span className="flex items-center gap-1 text-amber-500/80"><Eye className="w-3 h-3" />
                      {user.is_pro ? `${j.view_count || 0} views` : (
                        <span className="text-neutral-500">views · <span className="text-amber-500">Pro</span></span>
                      )}
                    </span>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-neutral-500 group-hover:text-amber-500 shrink-0 mt-1" />
              </Link>
            ))}
          </Panel>
        )}

        {(user.role === "worker" || user.role === "admin") && (
          <Panel title="My Availability Posts" empty="You have no availability posted yet." span="md:col-span-6" data={myProfiles} testid="my-profiles-panel">
            {myProfiles.map((w) => (
              <Link key={w.id} to={`/workers/${w.id}`} data-testid={`my-profile-${w.id}`} className="group flex items-start justify-between gap-4 p-4 border-b border-neutral-800 last:border-0 hover:bg-neutral-900 transition-colors">
                <div className="min-w-0">
                  <div className="text-xs font-bold uppercase tracking-widest text-amber-500">{w.trade}</div>
                  <div className="display text-lg group-hover:text-amber-500 transition-colors truncate">{w.headline}</div>
                  <div className="text-xs uppercase tracking-wide text-neutral-400 mt-1 flex gap-3">
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{w.county}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{w.availability}</span>
                    {w.day_rate && <span className="flex items-center gap-1"><Euro className="w-3 h-3" />{w.day_rate}</span>}
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-neutral-500 group-hover:text-amber-500 shrink-0 mt-1" />
              </Link>
            ))}
          </Panel>
        )}

        <Panel title="Applications Received" empty="No applications received yet." span="md:col-span-6" data={received} testid="received-panel">
          {received.map((a) => (
            <div key={a.id} data-testid={`received-${a.id}`} className="p-4 border-b border-neutral-800 last:border-0">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs uppercase tracking-widest text-neutral-500">{a.target_type === "job" ? "For Job" : "Regarding Profile"}</div>
                  <div className="display text-lg">{a.target_title}</div>
                </div>
                <div className="text-xs uppercase tracking-widest text-amber-500">{a.applicant_name}</div>
              </div>
              {a.message && <p className="text-sm text-neutral-300 mt-2 italic">&ldquo;{a.message}&rdquo;</p>}
              <div className="mt-3 flex flex-wrap gap-3 text-xs">
                <a href={`mailto:${a.applicant_email}`} className="inline-flex items-center gap-1 text-neutral-300 hover:text-amber-500"><Mail className="w-3 h-3" />{a.applicant_email}</a>
                {a.applicant_phone && <a href={`tel:${a.applicant_phone}`} className="inline-flex items-center gap-1 text-neutral-300 hover:text-amber-500"><Phone className="w-3 h-3" />{a.applicant_phone}</a>}
              </div>
            </div>
          ))}
        </Panel>

        <Panel title="Applications Sent" empty="You haven't applied to any jobs or contacted workers yet." span="md:col-span-6" data={sent} testid="sent-panel">
          {sent.map((a) => (
            <div key={a.id} data-testid={`sent-${a.id}`} className="p-4 border-b border-neutral-800 last:border-0">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs uppercase tracking-widest text-neutral-500">{a.target_type === "job" ? "Applied to job" : "Contacted worker"}</div>
                  <div className="display text-lg">{a.target_title}</div>
                </div>
                <div className="text-xs uppercase tracking-widest text-amber-500">{a.contact_name}</div>
              </div>
              <div className="mt-3 flex flex-wrap gap-3 text-xs">
                {a.contact_email && <a href={`mailto:${a.contact_email}`} className="inline-flex items-center gap-1 text-neutral-300 hover:text-amber-500"><Mail className="w-3 h-3" />{a.contact_email}</a>}
                {a.contact_phone && <a href={`tel:${a.contact_phone}`} className="inline-flex items-center gap-1 text-neutral-300 hover:text-amber-500"><Phone className="w-3 h-3" />{a.contact_phone}</a>}
              </div>
            </div>
          ))}
        </Panel>
      </div>

      {reviewing && (
        <ReviewModal
          pending={reviewing}
          onClose={() => setReviewing(null)}
          onSubmitted={() => { loadPending(); refresh?.(); }}
        />
      )}
    </div>
  );
}

function Stat({ label, value, Icon, span, show = true }) {
  if (!show) return null;
  return (
    <div className={`${span} border border-neutral-800 bg-neutral-900 p-5`}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-neutral-400">{label}</div>
        <Icon className="w-4 h-4 text-amber-500" />
      </div>
      <div className="display text-5xl text-amber-500 mt-2">{String(value).padStart(2, "0")}</div>
    </div>
  );
}

function Panel({ title, empty, span, data, children, testid }) {
  return (
    <div className={`${span} border border-neutral-800 bg-neutral-900`} data-testid={testid}>
      <div className="px-5 py-4 border-b border-neutral-800">
        <div className="display text-xl">{title}</div>
      </div>
      {(!data || data.length === 0) ? (
        <div className="p-6 text-sm text-neutral-500">{empty}</div>
      ) : (
        <div>{children}</div>
      )}
    </div>
  );
}
